import React, { useId, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import Tab from 'components/partials/tabs/tab/tab';

interface TabItem {
  value: string;
  label: string;
  icon?: React.ReactNode;
  count?: number;
  isDisabled?: boolean;
  panelId?: string;
  ariaLabel?: string;
}

interface TabsProps {
  items: TabItem[];
  activeTab: string;
  onSelect: (value: string) => void;
  customClass?: string;
  'data-testid'?: string;
}

const Tabs = ({
  items,
  activeTab,
  onSelect,
  customClass,
  'data-testid': dataTestId = 'tabs',
}: TabsProps): React.JSX.Element => {
  const baseId = useId();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const enabledIndexes = items.reduce<number[]>((acc, item, index) => {
    if (!item.isDisabled) {
      acc.push(index);
    }

    return acc;
  }, []);

  // Roving tabindex + arrow-key navigation per the WAI-ARIA Tabs pattern:
  // https://www.w3.org/WAI/ARIA/apg/patterns/tabs/
  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLUListElement>
  ): void => {
    if (!enabledIndexes.length) {
      return;
    }

    const currentIndex = items.findIndex((item) => item.value === activeTab);
    const currentPosition = enabledIndexes.indexOf(currentIndex);

    if (currentPosition === -1) {
      return;
    }

    let nextPosition: number;

    switch (event.key) {
      case 'ArrowRight':
        nextPosition = (currentPosition + 1) % enabledIndexes.length;
        break;
      case 'ArrowLeft':
        nextPosition =
          (currentPosition - 1 + enabledIndexes.length) % enabledIndexes.length;
        break;
      case 'Home':
        nextPosition = 0;
        break;
      case 'End':
        nextPosition = enabledIndexes.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextIndex = enabledIndexes[nextPosition];

    tabRefs.current[nextIndex]?.focus();
    onSelect(items[nextIndex].value);
  };

  return (
    <div
      className={twMerge(
        'text-base text-center text-navy-500 border-b w-full',
        customClass
      )}
      data-testid={dataTestId}
    >
      {/* <ul role="tablist"> is the WAI-ARIA APG Tabs pattern
          (https://www.w3.org/WAI/ARIA/apg/patterns/tabs/). Switching to a
          <div> container instead trips jsx-a11y/interactive-supports-focus,
          because a tablist container is intentionally not itself in the tab
          order (only the Tab buttons are, via roving tabindex below). */}
      <ul
        className="flex flex-wrap -mb-px gap-5"
        role="tablist" /* NOSONAR: correct WAI-ARIA APG Tabs pattern, not an interactive-role violation */
        aria-orientation="horizontal"
        onKeyDown={handleKeyDown}
      >
        {items.map((item, index) => {
          const isActive = activeTab === item.value;
          const tabId = `${baseId}-tab-${item.value}`;

          return (
            <Tab
              key={item.value}
              ref={(node) => {
                tabRefs.current[index] = node;
              }}
              id={tabId}
              panelId={item.panelId}
              label={item.label}
              value={item.value}
              icon={item.icon}
              count={item.count}
              isActive={isActive}
              isDisabled={item.isDisabled}
              onClick={() => onSelect(item.value)}
              aria-label={item.ariaLabel}
            />
          );
        })}
      </ul>
    </div>
  );
};

export default React.memo(Tabs);
